import React from 'react';
import { LinkedinIcon, InstagramIcon, FacebookIcon } from 'lucide-react';

// Vestigingsgegevens moeten exact overeenkomen met noorddigital.nl,
// het Google Bedrijfsprofiel en de KvK. Hier stond eerder "Alkmaar",
// terwijl de vestiging in Oudkarspel zit. Afwijkende gegevens tussen
// vindplaatsen schaden de lokale vindbaarheid.
const coreprintLinks = [{
  label: 'Wat CorePrint is',
  href: '#wat-is-coreprint'
}, {
  label: 'Hoe het werkt',
  href: '#hoe'
}, {
  label: 'Voor wie',
  href: '#voor-wie'
}, {
  label: 'Wat je krijgt',
  href: '#bewijs'
}, {
  label: 'Plan een presentatie',
  href: '#gesprek'
}];
const noordLinks = [{
  label: 'Over ons',
  href: 'https://noorddigital.nl/over-ons'
}, {
  label: 'Ons team',
  href: 'https://noorddigital.nl/over-ons#team'
}, {
  label: 'Klantcases',
  href: 'https://noorddigital.nl/#client-cases'
}, {
  label: 'Kennisbank',
  href: 'https://noorddigital.nl/bibliotheek'
}, {
  label: 'Vacatures',
  href: 'https://noorddigital.nl/sollicitatie'
}, {
  label: 'Webdesign Alkmaar',
  href: 'https://webdesignalkmaar.nl/',
  subtle: true
}];
const socials = [{
  Icon: LinkedinIcon,
  href: 'https://www.linkedin.com/company/noord-digital/',
  label: 'LinkedIn'
}, {
  Icon: FacebookIcon,
  href: 'https://www.facebook.com/profile.php?id=61572493568612',
  label: 'Facebook'
}, {
  Icon: InstagramIcon,
  href: 'https://www.instagram.com/noord_digital/',
  label: 'Instagram'
}];
export function Footer() {
  const jaar = new Date().getFullYear();
  return <footer style={{
    background: '#0D1628',
    borderTop: '1px solid rgba(255, 255, 255, 0.06)'
  }} data-id="element-132" data-mp="33nm44yp1hk6">
      <div className="max-w-[1200px] mx-auto px-6 py-20" data-id="element-133" data-mp="ctqm4p48fvs0">
        <div className="grid md:grid-cols-4 gap-12 mb-16" data-id="element-134" data-mp="cu8swfoitse6">
          {/* Brand */}
          <div className="md:col-span-1" data-id="element-135" data-mp="qub8fv9cw9q9">
            <div className="flex items-center gap-4 mb-5" data-id="element-136" data-mp="qr5h09d98vrd">
              <div className="text-white font-bold text-2xl tracking-tight" data-id="element-137" data-mp="mt7q923u61lv">
                Core<span className="text-brand-arctic" data-id="element-138" data-mp="tvw8wk94jlhk">Print</span>
              </div>
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/5 border border-white/10" data-id="element-139" data-mp="yto87zc3m9vs">
                <img src="https://cdn.magicpatterns.com/uploads/fJjgNKZLLccSeJuGWQEqQf/Norden_groed_hallo.png" alt="Norden, de mascotte van Noord Digital" loading="lazy" className="w-9 h-9 object-contain opacity-90" data-id="element-140" data-mp="13sir11a2fyfw" />
              </span>
            </div>

            <p className="text-white/45 text-sm leading-relaxed mb-7" data-id="element-141" data-mp="1mjscm8x42dnu">
              Online groei op basis van gemeten vraag. Een dienst van{' '}
              <a href="https://noorddigital.nl" target="_blank" rel="noopener noreferrer" className="text-white/65 hover:text-white underline-offset-4 hover:underline transition-colors" data-id="element-142" data-mp="1rhtrsirf1su1">
                Noord Digital
              </a>
              .
            </p>
            <div className="flex gap-3" data-id="element-143" data-mp="2agyfna1uv5qc">
              {socials.map(({
              Icon,
              href,
              label
            }) => <a key={label} href={href} target="_blank" rel="noopener noreferrer" aria-label={`Noord Digital op ${label}`} className="w-10 h-10 rounded-xl flex items-center justify-center transition-colors duration-200 hover:bg-white/10" style={{
              border: '1px solid rgba(255, 255, 255, 0.08)',
              background: 'rgba(255, 255, 255, 0.02)'
            }} data-id="element-144" data-mp="25mkcglgybub4">
                  <Icon size={17} className="text-white/45" data-id="element-145" data-mp="2fd59w5kw2ssg" />
                </a>)}
            </div>
          </div>

          {/* CorePrint */}
          <nav aria-label="CorePrint" data-id="element-146" data-mp="18vrzvxt88ke3">
            <div className="text-white font-semibold mb-6 text-sm" data-id="element-147" data-mp="13y05mx540m6q">
              CorePrint
            </div>
            <ul className="space-y-3.5" data-id="element-148" data-mp="2ruxx4414fgsg">
              {coreprintLinks.map(l => <li key={l.label} data-id="element-149" data-mp="2wmptwwaveir1">
                  <a href={l.href} className="block text-white/45 hover:text-white text-sm transition-colors" data-id="element-150" data-mp="2wnr30v8bsv64">
                    {l.label}
                  </a>
                </li>)}
            </ul>
          </nav>

          {/* Noord Digital */}
          <nav aria-label="Noord Digital" data-id="element-151" data-mp="3bhvu32nabncg">
            <div className="text-white font-semibold mb-6 text-sm" data-id="element-152" data-mp="3fkai015t9dal">
              Noord Digital
            </div>
            <ul className="space-y-3.5" data-id="element-153" data-mp="31krbfcsnea0e">
              {noordLinks.map(l => <li key={l.label} data-id="element-154" data-mp="36jko53041wzi">
                  <a href={l.href} target="_blank" rel="noopener noreferrer" className={`block text-sm transition-colors ${l.subtle ? 'text-white/25 hover:text-white/45' : 'text-white/45 hover:text-white'}`} data-id="element-155" data-mp="36mqdmjcnj0vr">
                    {l.label}
                  </a>
                </li>)}
            </ul>
          </nav>

          {/* Contact */}
          <div data-id="element-156" data-mp="1wmw2b89mcsn1">
            <div className="text-white font-semibold mb-6 text-sm" data-id="element-157" data-mp="1wlckhd8be9j5">Contact</div>
            <address className="not-italic space-y-3.5 mb-8" data-id="element-158" data-mp="1rnb2kvmmkg99">
              <a href="#gesprek" className="block text-white/45 hover:text-white text-sm transition-colors" data-id="element-159" data-mp="1rlq2yrlge74j">
                Kennismaking plannen
              </a>
              <a href="mailto:info@noorddigital.nl" className="block text-white/45 hover:text-white text-sm transition-colors" data-id="element-160" data-mp="1dni1q7hdwjnk">
                info@noorddigital.nl
              </a>
              <a href="tel:+31653689686" className="block text-white/45 hover:text-white text-sm transition-colors" data-id="element-161" data-mp="l6ffuuzndbqo">
                06 53689686
              </a>
              <div className="text-white/45 text-sm leading-relaxed" data-id="element-162" data-mp="2g9u9rog023t7">
                Schuit 8a
                <br data-id="element-163" data-mp="2l8fbxtetaxka" />
                1724 BD Oudkarspel
                <br data-id="element-164" data-mp="2q0gn1c08ui8q" />
                <span className="text-white/30" data-id="element-165" data-mp="2q06rza0cdkw6">Gemeente Dijk en Waard</span>
              </div>
            </address>
            <a href="#gesprek" className="inline-flex items-center gap-2.5 bg-brand-primary text-white font-semibold text-sm px-6 py-3 rounded-lg hover:opacity-90 transition-opacity duration-200" data-id="element-166" data-mp="2uyi4xtlxobim">
              Plan een presentatie
            </a>
          </div>
        </div>

        <div className="pt-8 flex flex-col md:flex-row items-center justify-between gap-5" style={{
        borderTop: '1px solid rgba(255, 255, 255, 0.06)'
      }} data-id="element-167" data-mp="278e23ogkkfso">
          <p className="text-white/30 text-sm" data-id="element-168" data-mp="3iouk9pjidaj8">
            © {jaar} Noord Digital · KvK 96071044 · CorePrint is een dienst van{' '}
            <a href="https://noorddigital.nl" target="_blank" rel="noopener noreferrer" className="hover:text-white/55 transition-colors" data-id="element-169" data-mp="1b347gnu52iyc">
              Noord Digital
            </a>
          </p>
          <div className="flex gap-8 flex-wrap justify-center" data-id="element-170" data-mp="1297pll80bsoq">
            {[{
            label: 'Privacybeleid',
            href: 'https://noorddigital.nl/privacy'
          }, {
            label: 'Algemene voorwaarden',
            href: 'https://noorddigital.nl/algemene-voorwaarden'
          }, {
            label: 'Cookiebeleid',
            href: 'https://noorddigital.nl/cookiebeleid'
          }].map(l => <a key={l.label} href={l.href} target="_blank" rel="noopener noreferrer" className="text-white/30 hover:text-white/55 text-sm transition-colors" data-id="element-171" data-mp="2dq7e05r8hndh">
                {l.label}
              </a>)}
          </div>
        </div>
      </div>
    </footer>;
}